import { captureException } from '@sentry/react';
import { observer } from 'mobx-react-lite';
import { useRef, useState } from 'react';
import { toast } from 'sonner';

import { backendApis } from '@/backendApis';
import { BaaFeatureLimits } from '@/components/blocks/baa-feature-limits';
import { getEnvVar } from '@/env';
import { userStore } from '@/stores/user.store';

import { Button } from '../ui/Button';
import { Checkbox } from '../ui/Checkbox';
import { Dialog } from '../ui/dialog';
import { Input } from '../ui/Input';
import { YStack } from '../ui/layout/layout';

import { type ResolvableModalFC } from './modalsManager';

const BAA_PDF_URL = `${getEnvVar('VITE_API_URL')}/static/littlebird-baa.pdf`;

interface BaaAgreementModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resolve: (signed: boolean) => void;
}

export const BaaAgreementModal: ResolvableModalFC<
  BaaAgreementModalProps,
  boolean
> = observer(({ open, onOpenChange, resolve }) => {
  const [legalName, setLegalName] = useState('');
  const [acknowledgedLimits, setAcknowledgedLimits] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const hasResolved = useRef(false);

  const trimmedName = legalName.trim();
  const canSubmit =
    trimmedName.length > 0 && acknowledgedLimits && !isSubmitting;

  const finish = (signed: boolean) => {
    hasResolved.current = true;
    resolve(signed);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next && !hasResolved.current) finish(false);
    onOpenChange(next);
  };

  const handleAgree = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      await backendApis.user.signBaa(trimmedName);
      await userStore.refresh();
      toast.success('HIPAA BAA signed');
      onOpenChange(false);
      finish(true);
    } catch (error) {
      captureException(error);
      toast.error(
        error instanceof Error ? error.message : 'Failed to sign BAA'
      );
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Content className="gap-4 max-w-lg max-h-[calc(100dvh-2rem)] overflow-y-auto">
        <Dialog.Header className="text-left">
          <Dialog.Title>HIPAA Business Associate Agreement</Dialog.Title>
        </Dialog.Header>

        <YStack className="gap-3">
          <p className="text-sm text-foreground">
            Click here to view the{' '}
            <a
              href={BAA_PDF_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-accent hover:text-accent/80"
            >
              HIPAA Business Associate Agreement
            </a>
            .
          </p>

          <YStack className="gap-2 rounded-lg border border-border p-3 text-sm">
            <p className="font-medium">
              HIPAA privacy mode limits some features
            </p>
            <p className="text-muted-foreground">
              Signing enables HIPAA privacy mode. Littlebird stops using certain
              third-party services, so:
            </p>
            <BaaFeatureLimits />
            <p>You cannot turn this mode off in Settings.</p>
          </YStack>

          <Input
            autoFocus
            placeholder="Enter your legal name"
            value={legalName}
            disabled={isSubmitting}
            onChange={(e) => setLegalName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canSubmit) handleAgree();
            }}
          />

          <label
            htmlFor="baa-acknowledge-limits"
            className="flex items-start gap-2 text-sm cursor-pointer"
          >
            <Checkbox
              id="baa-acknowledge-limits"
              className="mt-0.5"
              checked={acknowledgedLimits}
              disabled={isSubmitting}
              onCheckedChange={(checked) =>
                setAcknowledgedLimits(checked === true)
              }
            />
            I understand these limits and that I cannot turn this mode off in
            Settings.
          </label>

          <p className="text-sm text-muted-foreground">
            By clicking "I agree", you agree to the HIPAA Business Associate
            Agreement.
          </p>
        </YStack>

        <Dialog.Footer className="mt-1">
          <Button
            className="w-full"
            disabled={!canSubmit}
            onClick={handleAgree}
          >
            {isSubmitting ? 'Signing...' : 'I agree'}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
});
